import React, { TextareaHTMLAttributes, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { nanoid } from 'nanoid'
import { ErrorDetail } from '../ErrorDetail/ErrorDetail'

const StyledTextArea = styled.textarea<Omit<Props, 'label'>>`
  width: 100%;
  font-size: var(--default-text);
  font-family: ${({ theme }) => theme.fonts.primary};
  background-color: ${({ theme }) => theme.colors.surface.subdued};
  color: ${({ theme }) => theme.colors.font.regular};
  padding: 0.625em;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 12px;

  ::placeholder {
    color: ${({ theme }) => theme.colors.font.placeholder};
  }

  &:hover,
  &:focus {
    border: 2px solid ${({ theme }) => theme.colors.system.interactive.focused};
  }

  &:active {
    border: 2px solid ${({ theme }) => theme.colors.system.interactive.selected};
  }

  ${(props) =>
    props.disabled &&
    css`
      border: 2px solid
        ${({ theme }) => theme.colors.system.interactive.disabled};

      &:hover,
      &:focus,
      &:active {
        border: 2px solid
          ${({ theme }) => theme.colors.system.interactive.disabled};
      }
    `}
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25em;

  > label {
    color: ${({ theme }) => theme.colors.font.regular};
    font-size: var(--small-text);
    font-weight: 700;
  }
`

const Field = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  row-gap: 0.25em;
`

export interface Props extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  hideLabel?: boolean
  error?: React.ReactNode
}

export const TextArea: React.FC<Props> = React.forwardRef<
  HTMLTextAreaElement,
  Props
>(
  (
    {
      className,
      error,
      hideLabel,
      label,
      name,
      required,
      rows,
      value,
      id,
      onBlur,
      onChange,
      ...rest
    },
    ref,
  ) => {
    const textAreaId = useMemo(() => {
      return id ? id : `TextArea-id-${nanoid()}`
    }, [id])

    return (
      <Field>
        <Container className={className}>
          <label htmlFor={textAreaId} hidden={hideLabel}>
            {label}
          </label>
          <StyledTextArea
            id={textAreaId}
            aria-label={!hideLabel ? label : undefined}
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            ref={ref}
            rows={rows ?? 5}
            autoCorrect={rest.autoCorrect ?? 'on'}
            {...rest}
          >
            {/* {value} */}
          </StyledTextArea>
        </Container>
        {error ? <ErrorDetail error={error} /> : null}
      </Field>
    )
  },
)

TextArea.displayName = 'TextArea'
